import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, Timestamp, getDocs, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface Message {
  id: string;
  content: string;
  senderId: string;
  receiverId: string;
  timestamp: Timestamp;
  read: boolean;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
}

interface MessagesContextType {
  messages: Message[];
  sendMessage: (receiverId: string, content: string, file?: File) => Promise<void>;
  markMessagesAsRead: (senderId: string) => Promise<void>;
}

const MessagesContext = createContext<MessagesContextType | null>(null);

export function MessagesProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) return;

    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('participants', 'array-contains', currentUser.uid),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            content: data.content || '',
            senderId: data.senderId || '',
            receiverId: data.receiverId || '',
            timestamp: data.timestamp || Timestamp.now(),
            read: data.read || false,
            fileUrl: data.fileUrl,
            fileName: data.fileName,
            fileType: data.fileType
          } as Message;
        })
        .sort((a, b) => a.timestamp.seconds - b.timestamp.seconds);

      // Atualizar o contador de mensagens não lidas para cada contato
      const unreadCounts = newMessages.reduce((acc, message) => {
        // Só conta como não lida se o usuário atual for o destinatário
        if (message.receiverId === currentUser.uid && !message.read) {
          acc[message.senderId] = (acc[message.senderId] || 0) + 1;
        }
        return acc;
      }, {} as { [key: string]: number });

      // Atualizar os contatos com as contagens não lidas
      Object.entries(unreadCounts).forEach(async ([senderId, count]) => {
        const contactsRef = collection(db, 'contacts');
        const contactQuery = query(
          contactsRef,
          where('userId', '==', currentUser.uid),
          where('contactId', '==', senderId)
        );

        const contactSnapshot = await getDocs(contactQuery);
        if (!contactSnapshot.empty) {
          const contactDoc = contactSnapshot.docs[0];
          await updateDoc(doc(db, 'contacts', contactDoc.id), {
            unreadCount: count
          });
        }
      });

      setMessages(newMessages);
    }, (error) => {
      console.error('Erro crítico ao observar mensagens:', error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const sendMessage = async (receiverId: string, content: string, file?: File) => {
    if (!currentUser) return;

    try {
      let fileUrl = '';
      let fileName = '';
      let fileType = '';

      if (file) {
        // Upload do arquivo para o Storage
        const fileRef = ref(storage, `messages/${currentUser.uid}/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        fileUrl = await getDownloadURL(fileRef);
        fileName = file.name;
        fileType = file.type;
      }

      const messageData = {
        content,
        senderId: currentUser.uid,
        receiverId,
        timestamp: Timestamp.now(),
        read: false,
        participants: [currentUser.uid, receiverId],
        ...(fileUrl && { fileUrl, fileName, fileType })
      };

      await addDoc(collection(db, 'messages'), messageData);

      // Atualizar o contador de mensagens não lidas
      const contactsRef = collection(db, 'contacts');
      const q = query(
        contactsRef,
        where('userId', '==', receiverId),
        where('contactId', '==', currentUser.uid)
      );
      
      const contactSnapshot = await getDocs(q);
      
      if (!contactSnapshot.empty) {
        const contactDoc = contactSnapshot.docs[0];
        await updateDoc(doc(db, 'contacts', contactDoc.id), {
          unreadCount: (contactDoc.data().unreadCount || 0) + 1,
          lastMessage: {
            content: content || 'Anexo enviado',
            timestamp: Timestamp.now(),
            senderId: currentUser.uid
          }
        });
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      throw error;
    }
  };

  const markMessagesAsRead = async (senderId: string) => {
    if (!currentUser) return;

    try {
      const batch = writeBatch(db);
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('senderId', '==', senderId),
        where('receiverId', '==', currentUser.uid),
        where('read', '==', false)
      );

      const unreadMessages = await getDocs(q);
      unreadMessages.docs.forEach((doc) => {
        batch.update(doc.ref, { read: true });
      });

      // Zerar o contador de mensagens não lidas no contato
      const contactsRef = collection(db, 'contacts');
      const contactQuery = query(
        contactsRef,
        where('userId', '==', currentUser.uid),
        where('contactId', '==', senderId)
      );

      const contactSnapshot = await getDocs(contactQuery);
      if (!contactSnapshot.empty) {
        const contactDoc = contactSnapshot.docs[0];
        batch.update(doc(db, 'contacts', contactDoc.id), {
          unreadCount: 0
        });
      }

      await batch.commit();
    } catch (error) {
      console.error('Erro ao marcar mensagens como lidas:', error);
    }
  };

  const value = {
    messages,
    sendMessage,
    markMessagesAsRead
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
}

export function useMessages() {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }
  return context;
}
